<template>
  <div class="chat-connection-card">
    <div
      v-if="crmCards.length"
      class="chat-connection-card__section"
    >
      <div class="chat-connection-card__grid">
        <CrmWrapper
          v-for="card in crmCards"
          :key="card.provider"
          :provider="card.provider"
        >
          <template
            #activator="{
              props: activatorProps,
              isConnected,
              loading,
              connect,
              settings,
              disconnect,
            }"
          >
            <Connection
              :active="isConnected"
              :image="card.image"
              :loading="loading"
              :dialog-activator-props="activatorProps"
              :connection-button-text="`connection-manager.crm.${card.provider.toLowerCase()}.connect`"
              card-background="#ffffff"
              show-settings
              @connect="() => onProviderConnect('crm', card.provider, connect)"
              @settings="settings"
              @disconnect="disconnect"
            />
          </template>
        </CrmWrapper>
      </div>
    </div>

    <div
      v-if="socialCards.length"
      class="chat-connection-card__section"
    >
      <div class="chat-connection-card__grid">
        <SocialMediaWrapper
          v-for="card in socialCards"
          :key="card.provider"
          :provider="card.provider"
          :connected-media="socialMediaConnections"
        >
          <template
            #activator="{ props: activatorProps, isConnected, loading, connect, disconnect }"
          >
            <Connection
              :active="isConnected"
              :image="card.image"
              :loading="loading"
              :dialog-activator-props="activatorProps"
              verified-text="connection-manager.connected"
              card-background="#ffffff"
              @connect="() => onProviderConnect('social', card.provider, connect)"
              @disconnect="disconnect"
            />
          </template>
        </SocialMediaWrapper>
      </div>
    </div>

    <div class="chat-connection-card__actions mt-4 d-flex flex-wrap align-center ga-3">
      <v-btn
        color="primary"
        rounded="lg"
        :disabled="busy"
        @click="finish"
      >
        {{ finishLabel }}
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

import { useCrm } from '@/composables/useCrm'
import { useSocialMedia } from '@/composables/useSocialMedia'
import { CrmProvider, SocialMediaType } from '@/generated/graphql'
import type { ConnectionType } from '@/lib/ai/connectionTool'
import { normalizeConnectedProviders } from '@/lib/ai/connectionTool'
import eventBus, { eventNames } from '@/lib/eventBus'
import Connection from '@/modules/connectionManager/Connection.vue'
import CrmWrapper from '@/modules/connectionManager/crmWrapper/index.vue'
import SocialMediaWrapper from '@/modules/connectionManager/socialMediaWrapper/index.vue'

export type ConnectionCardEvent = {
  type: ConnectionType
  provider: string
  connected: string[]
}

const props = defineProps<{
  crmProviders?: string[]
  socialProviders?: string[]
  busy?: boolean
}>()

const emit = defineEmits<{
  start: [payload: ConnectionCardEvent]
  connected: [payload: ConnectionCardEvent]
  failed: [payload: ConnectionCardEvent]
  finish: [payload: { type: ConnectionType; connected: string[] }]
}>()

const { t } = useI18n()
const { isConnected: crmIsConnected, currentProvider } = useCrm()
const { socialMediaConnections, loadSocialMediaConnections, refetchSocialMediaConnections } =
  useSocialMedia()

// Logos mirror the connection-manager segments (index.vue / CrmConnectionsSegment.vue).
const CRM_IMAGES: Record<string, string> = {
  ONOFFICE: '/img/onOffice-new.png',
  PROPSTACK: '/img/propstack.png',
  FLOWFACT: '/img/flowfact.png',
  CASAONE: '/img/casaone.svg',
  EMPRO: '/img/empro.png',
}
const SOCIAL_IMAGES: Record<string, string> = {
  FACEBOOK: '/img/facebook.png',
  INSTAGRAM: '/img/instagram.png',
  LINKEDIN: '/img/linkedin.png',
}

const crmCards = computed(() =>
  (props.crmProviders ?? [])
    .map((p) => String(p).toUpperCase())
    .filter((p) => CRM_IMAGES[p])
    .map((p) => ({ provider: p as CrmProvider, image: CRM_IMAGES[p] }))
)

const socialCards = computed(() =>
  (props.socialProviders ?? [])
    .map((p) => String(p).toUpperCase())
    .filter((p) => SOCIAL_IMAGES[p])
    .map((p) => ({ provider: p as SocialMediaType, image: SOCIAL_IMAGES[p] }))
)

const isCrmCard = computed(() => crmCards.value.length > 0)
// A card renders exactly one type (genaisis sends either CRM or social providers,
// never both). Resolve the type from what it actually renders.
const cardType = computed<ConnectionType>(() => (isCrmCard.value ? 'crm' : 'social'))

// Providers connected on THIS card — scoped to the card's type AND the providers it
// actually renders. Never report a globally-connected account this card doesn't show:
// that would let the wrong step complete or hand Genesis a foreign provider name.
const connectedProviders = computed<string[]>(() => {
  if (cardType.value === 'crm') {
    const provider = String(currentProvider.value ?? '').toUpperCase()
    const rendered = crmCards.value.some((c) => String(c.provider) === provider)
    return crmIsConnected() && provider && rendered ? [provider] : []
  }
  const rendered = new Set(socialCards.value.map((c) => String(c.provider)))
  return normalizeConnectedProviders(
    socialMediaConnections.value.map((s) => String(s).toUpperCase()).filter((s) => rendered.has(s))
  )
})

const finishLabel = computed(() => {
  if (connectedProviders.value.length > 0) {
    return t('onboarding.wizard.chat-widgets.connection-continue')
  }
  if (isCrmCard.value) return t('onboarding.wizard.chat-widgets.connection-no-crm')
  return t('onboarding.wizard.chat-widgets.connection-skip')
})

const finish = () => emit('finish', { type: cardType.value, connected: connectedProviders.value })

const onProviderConnect = (type: ConnectionType, provider: string, connect: () => unknown) => {
  emit('start', {
    type,
    provider: String(provider).toUpperCase(),
    connected: connectedProviders.value,
  })
  return connect()
}

const providerFromBus = (payload: unknown): string =>
  String((payload as { provider?: unknown } | undefined)?.provider ?? '')
    .trim()
    .toUpperCase()

const onCrmConnected = () => {
  if (!crmCards.value.length) return
  if (!crmIsConnected() || !currentProvider.value) return
  emit('connected', {
    type: 'crm',
    provider: String(currentProvider.value).toUpperCase(),
    connected: connectedProviders.value,
  })
}

const onSocialConnected = async (payload: unknown) => {
  if (!socialCards.value.length) return
  const provider = providerFromBus(payload)
  if (!provider || !socialCards.value.some((c) => String(c.provider) === provider)) return
  try {
    await refetchSocialMediaConnections()
  } catch {
    // Report even if the refresh rejects — the connection already succeeded.
  }
  emit('connected', {
    type: 'social',
    provider,
    connected: normalizeConnectedProviders([...connectedProviders.value, provider]),
  })
}

const onCrmFailed = (payload: unknown) => {
  if (!crmCards.value.length) return
  const provider = providerFromBus(payload)
  if (provider && !crmCards.value.some((c) => String(c.provider) === provider)) return
  emit('failed', {
    type: 'crm',
    provider,
    connected: connectedProviders.value,
  })
}

const onSocialFailed = (payload: unknown) => {
  if (!socialCards.value.length) return
  const provider = providerFromBus(payload)
  if (!provider || !socialCards.value.some((c) => String(c.provider) === provider)) return
  emit('failed', {
    type: 'social',
    provider,
    connected: connectedProviders.value,
  })
}

onMounted(() => {
  void loadSocialMediaConnections()
  eventBus.$on(eventNames.CRM_UPDATED, onCrmConnected)
  eventBus.$on(eventNames.SOCIAL_MEDIA_UPDATED, onSocialConnected)
  eventBus.$on(eventNames.CRM_CONNECTION_FAILED, onCrmFailed)
  eventBus.$on(eventNames.SOCIAL_MEDIA_FAILED, onSocialFailed)
})

onBeforeUnmount(() => {
  eventBus.$off(eventNames.CRM_UPDATED, onCrmConnected)
  eventBus.$off(eventNames.SOCIAL_MEDIA_UPDATED, onSocialConnected)
  eventBus.$off(eventNames.CRM_CONNECTION_FAILED, onCrmFailed)
  eventBus.$off(eventNames.SOCIAL_MEDIA_FAILED, onSocialFailed)
})
</script>

<style scoped>
/* Pack providers into a compact multi-per-row grid (like the image tiles)
   instead of one 250px card per row, so the widget fits the narrow chat panel. */
.chat-connection-card__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 8px;
}

.chat-connection-card__section + .chat-connection-card__section {
  margin-top: 8px;
}

/* Compact the shared Connection card for the chat: let it fill the grid cell
   (drop the fixed 250px width) and shrink the logo + padding to save space. */
.chat-connection-card__grid :deep(.connection) {
  min-width: 0;
  max-width: none;
  width: 100%;
}

.chat-connection-card__grid :deep(.connection__content) {
  padding: 10px 6px;
  gap: 6px;
}

.chat-connection-card__grid :deep(.connection__image) {
  height: 36px !important;
  padding: 0 8px;
}

.chat-connection-card__grid :deep(.connection__image .v-img__img) {
  max-height: 36px !important;
}
</style>
